import { AppEntity, ContactInformationSchema, PostalAddressSchema } from '@app/icare/db/schema/common'
import { msg } from '@lingui/core/macro'
import { entityId } from '@pkg/db/schema'
import { defineEnum } from '@pkg/shared/utils/enum-builder'
import { z } from 'zod'

export const DoctorSpecialityEnum = defineEnum({
  WITH_OTHER_SPECIALITY: msg`Praxis mit weiteren Fachrichtungen`,
  GASTROENTEROLOGY: msg`Gastroenterologie`,
  GYNECOLOGY: msg`Gynäkologie`,
  GENERAL_MEDICINE: msg`Allgemeinmedizin`,
  INTERNAL_MEDICINE: msg`Innere Medizin`,
  MEDICAL_CARE_CENTRE: msg`Medizinisches Versorgungszentrum/Ärztezentrum`,
  NEPHROLOGY: msg`Nephrologische Ambulanz/Dialyse`,
  ONCOLOGY: msg`Onkologie`,
  PALLIATIVE_CARE: msg`Palliativnetzwerk`,
  UROLOGY: msg`Urologie`,
})

export type DoctorSpecialityType = typeof DoctorSpecialityEnum.$.Type

export const mapDoctorSpecialityFromSage = (val: string): DoctorSpecialityType | undefined => {
  switch (val) {
    case 'andPraxen':
      return DoctorSpecialityEnum.WITH_OTHER_SPECIALITY
    case 'Gastro':
      return DoctorSpecialityEnum.GASTROENTEROLOGY
    case 'Gyn':
      return DoctorSpecialityEnum.GYNECOLOGY
    case 'Hausärzte':
      return DoctorSpecialityEnum.GENERAL_MEDICINE
    case 'Innere Med':
      return DoctorSpecialityEnum.INTERNAL_MEDICINE
    case 'MVZ':
      return DoctorSpecialityEnum.MEDICAL_CARE_CENTRE
    case 'Nephro':
      return DoctorSpecialityEnum.NEPHROLOGY
    case 'Onko':
      return DoctorSpecialityEnum.ONCOLOGY
    case 'Pall.-Netz':
      return DoctorSpecialityEnum.PALLIATIVE_CARE
    case 'Urologie':
      return DoctorSpecialityEnum.UROLOGY
    default:
      return undefined
  }
}

export const mapDoctorSpecialityToSage = (val: DoctorSpecialityType) => {
  switch (val) {
    case DoctorSpecialityEnum.WITH_OTHER_SPECIALITY:
      return 'andPraxen'
    case DoctorSpecialityEnum.GASTROENTEROLOGY:
      return 'Gastro'
    case DoctorSpecialityEnum.GYNECOLOGY:
      return 'Gyn'
    case DoctorSpecialityEnum.GENERAL_MEDICINE:
      return 'Hausärzte'
    case DoctorSpecialityEnum.INTERNAL_MEDICINE:
      return 'Innere Med'
    case DoctorSpecialityEnum.MEDICAL_CARE_CENTRE:
      return 'MVZ'
    case DoctorSpecialityEnum.NEPHROLOGY:
      return 'Nephro'
    case DoctorSpecialityEnum.ONCOLOGY:
      return 'Onko'
    case DoctorSpecialityEnum.PALLIATIVE_CARE:
      return 'Pall.-Netz'
    case DoctorSpecialityEnum.UROLOGY:
      return 'Urologie'
    default:
      return undefined
  }
}

export const DoctorId = entityId('doctor')

export const DoctorSchema = AppEntity.extend({
  id: DoctorId.schema,
  speciality: z.enum(DoctorSpecialityEnum.$.zodEnum()),
  name: z.string().min(1).max(255).default(''),
  title: z.string().max(255).nullish().default(''),
  nameComplement: z.string().max(255).nullish(),
  address: PostalAddressSchema.nullish(),
  contact: ContactInformationSchema.nullish(),
  comment: z.string().nullish(),
})

export type Doctor = z.infer<typeof DoctorSchema>

export type DoctorIdType = typeof DoctorId.Type
